import { useEffect, useState, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot, updateDoc, addDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket, filterByPortals, isAllPortals, portalOf } from '../../contexts/MarketContext';
import useBasePath from '../../utils/useBasePath';
import { useT } from '../../contexts/LocaleContext';
import { useNavigate } from 'react-router-dom';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import { getCurrentWeekRates, toGBP } from '../../utils/exchangeRates';
import { sendConfirmationLetterEmail } from '../../utils/sendConfirmationLetterEmail';
import { usesParentLetters } from '../../utils/letterAssets';

// Owner (admin) financials filter — slice the money view by business unit.
const FIN_OPTIONS = [
  { key: 'all',          label: 'All' },
  { key: 'uk_to_spain',  label: 'UK to Spain' },
  { key: 'spain_to_uk',  label: 'Spain to UK' },
  { key: 'padelante',    label: 'Padelante' },
];

function tourLabel(req, fallback) {
  if (!req) return fallback || '—';
  return req.internalRef || req.tourName || [req.sport, req.destination].filter(Boolean).join(' — ') || fallback || '—';
}

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function fmt(n, sym = '£') {
  if (n == null) return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysUntil(str) {
  if (!str) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(str) - today) / 864e5);
}

function AgingPill({ dueDate, status }) {
  const t = useT();
  if (status === 'paid' || !dueDate) return null;
  const d = daysUntil(dueDate);
  if (d === null) return null;
  const overdue = d < 0, soon = d >= 0 && d <= 14;
  return (
    <span className={`ml-2 inline-flex items-center px-[7px] py-[2px] rounded-[20px] text-[10.5px] font-bold ${
      overdue ? 'bg-red-light text-dr' : soon ? 'bg-amber-light text-[#92400E]' : 'bg-surface text-[#6B7280]'
    }`}>
      {overdue ? t('invoices.agingOverdue', { n: -d }) : soon ? t('invoices.agingDueIn', { n: d }) : t('invoices.agingDue', { date: formatDate(dueDate) })}
    </span>
  );
}

function InvChip({ status }) {
  const t = useT();
  const paid = status === 'paid';
  return (
    <span className={`inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold ${
      paid ? 'bg-green-light text-sg' : 'bg-amber-light text-[#92400E]'
    }`}>
      <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${paid ? 'bg-sg' : 'bg-ba'}`} />
      {paid ? t('invoices.chipPaid') : t('invoices.chipUnpaid')}
    </span>
  );
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Invoices() {
  const { currentUser, isAdmin } = useAuth();
  const { portals } = useMarket();
  const base = useBasePath();
  const t = useT();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('grouped'); // 'grouped' | 'flat'
  const [expandedRequests, setExpandedRequests] = useState(new Set());
  const [downloadingId, setDownloadingId] = useState(null);
  const [financialsPortal, setFinancialsPortal] = useState('all'); // owner business filter
  const [rates, setRates] = useState(null); // this week's FX for the combined GBP view

  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(300));
    const unsub = onSnapshot(q, (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    getCurrentWeekRates().then(setRates).catch(console.error);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'requests'), (snap) => {
      setRequests(filterByPortals(snap.docs.map((d) => ({ id: d.id, ...d.data() })), portals));
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.join(',')]);

  const requestsById = useMemo(() => Object.fromEntries(requests.map((r) => [r.id, r])), [requests]);

  // Only show invoices whose parent tour is in the active portals. `requests` is
  // already portal-filtered, so requestsById membership enforces the portal set.
  // On the full roll-up (every portal), show every invoice so orphaned/legacy
  // ones still appear.
  const marketInvoices = useMemo(() => {
    return isAllPortals(portals) ? invoices : invoices.filter((i) => i.requestId && requestsById[i.requestId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, requestsById, portals.join(',')]);

  // Owner-only business filter. Only meaningful when the shell spans >1 portal
  // (the "All Tours" roll-up). Invoices carry market+brand, so we slice on
  // portalOf directly.
  const showFinancialsFilter = isAdmin && portals.length > 1;
  const scopedInvoices = useMemo(() => {
    if (!showFinancialsFilter || financialsPortal === 'all') return marketInvoices;
    return marketInvoices.filter((i) => portalOf(i) === financialsPortal);
  }, [marketInvoices, showFinancialsFilter, financialsPortal]);

  // "Combined" = the owner is looking at all businesses at once → show one GBP
  // total (Spain → UK invoices are in €). A single-business filter keeps native
  // currency.
  const combined = showFinancialsFilter && financialsPortal === 'all';
  const gbp = (inv) => toGBP(Number(inv.amount) || 0, inv.currency || 'GBP', rates || {});

  async function markPaid(invoiceId) {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (inv?.status === 'paid') return;
    await updateDoc(doc(db, 'invoices', invoiceId), {
      status: 'paid',
      paidAt: serverTimestamp(),
      markedPaidBy: currentUser.email,
    });
    await addDoc(collection(db, 'auditLog'), {
      action: 'invoice_marked_paid',
      invoiceId,
      performedByEmail: currentUser.email,
      performedByUid: currentUser.uid,
      timestamp: serverTimestamp(),
    });
    // The confirmation letter fires off the deposit specifically, not just
    // any instalment — same check as InvoicePanel's markPaid.
    const req = inv?.requestId ? requestsById[inv.requestId] : null;
    if (inv && inv.instalmentNumber === 1 && inv.invoiceType !== 'match_ticket' && usesParentLetters(req)) {
      try { await sendConfirmationLetterEmail(req); }
      catch (e) { console.error('Confirmation letter send failed:', e); }
    }
  }

  async function markUnpaid(invoiceId) {
    await updateDoc(doc(db, 'invoices', invoiceId), {
      status: 'unpaid',
      paidAt: null,
      markedPaidBy: null,
    });
    await addDoc(collection(db, 'auditLog'), {
      action: 'invoice_marked_unpaid',
      invoiceId,
      performedByEmail: currentUser.email,
      performedByUid: currentUser.uid,
      timestamp: serverTimestamp(),
    });
  }

  function toggleRequest(requestId) {
    setExpandedRequests((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });
  }

  const filtered = useMemo(
    () => filter === 'all' ? scopedInvoices : scopedInvoices.filter((i) => i.status === filter),
    [scopedInvoices, filter]
  );

  // Group totals by currency so multi-currency invoices display correctly
  const { outstandingByCurrency, paidByCurrency } = useMemo(() => {
    const outstanding = {};
    const paid = {};
    for (const inv of scopedInvoices) {
      const cur = inv.currency || 'GBP';
      const amt = Number(inv.amount) || 0;
      if (inv.status === 'unpaid') outstanding[cur] = (outstanding[cur] || 0) + amt;
      else if (inv.status === 'paid') paid[cur] = (paid[cur] || 0) + amt;
    }
    return { outstandingByCurrency: outstanding, paidByCurrency: paid };
  }, [scopedInvoices]);

  // Combined GBP totals (every business converted to £ at this week's rate).
  const { outstandingGBP, paidGBP } = useMemo(() => {
    let outstanding = 0, paid = 0;
    for (const inv of scopedInvoices) {
      if (inv.status === 'unpaid') outstanding += gbp(inv);
      else if (inv.status === 'paid') paid += gbp(inv);
    }
    return { outstandingGBP: outstanding, paidGBP: paid };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedInvoices, rates]);

  const groupedList = useMemo(() => {
    const grouped = filtered.reduce((acc, inv) => {
      const key = inv.requestId || '_unknown';
      if (!acc[key]) {
        acc[key] = { requestId: inv.requestId, summary: inv.requestSummary, customerEmail: inv.customerEmail, invoices: [] };
      }
      acc[key].invoices.push(inv);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => {
      const latestA = Math.max(...a.invoices.map((i) => i.createdAt?.seconds ?? 0));
      const latestB = Math.max(...b.invoices.map((i) => i.createdAt?.seconds ?? 0));
      return latestB - latestA;
    });
  }, [filtered]);

  const collectionRate = useMemo(() => {
    const total = scopedInvoices.reduce((s, i) => s + gbp(i), 0);
    const paid = scopedInvoices.filter((i) => i.status === 'paid').reduce((s, i) => s + gbp(i), 0);
    return total > 0 ? Math.round((paid / total) * 100) : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedInvoices, rates]);

  useEffect(() => {
    if (!loading && scopedInvoices.length > 0 && expandedRequests.size === 0) {
      const ids = new Set(scopedInvoices.map((i) => i.requestId).filter(Boolean));
      setExpandedRequests(ids);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  function Seg({ options, value, onChange, labelFor }) {
    return (
      <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[9px] p-[3px] gap-[2px]">
        {options.map((opt) => (
          <button key={opt} onClick={() => onChange(opt)}
            className={`px-3 py-[5px] rounded-[7px] text-[12px] font-semibold transition ${value === opt ? 'bg-navy text-white' : 'text-[#6B7280] hover:text-[#374151]'}`}>
            {labelFor ? labelFor(opt) : opt}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">{t('invoices.title')}</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('invoices.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-[10px]">
          {showFinancialsFilter && (
            <Seg
              options={FIN_OPTIONS.map((o) => o.label)}
              value={(FIN_OPTIONS.find((o) => o.key === financialsPortal) || FIN_OPTIONS[0]).label}
              onChange={(label) => setFinancialsPortal((FIN_OPTIONS.find((o) => o.label === label) || FIN_OPTIONS[0]).key)}
            />
          )}
          <Seg options={['By Request','All Invoices']} value={view === 'grouped' ? 'By Request' : 'All Invoices'} onChange={(v) => setView(v === 'By Request' ? 'grouped' : 'flat')}
            labelFor={(o) => o === 'By Request' ? t('invoices.viewByRequest') : t('invoices.viewAllInvoices')} />
          <Seg options={['All','Unpaid','Paid']} value={filter.charAt(0).toUpperCase() + filter.slice(1)} onChange={(v) => setFilter(v.toLowerCase())}
            labelFor={(o) => o === 'All' ? t('invoices.filterAll') : o === 'Unpaid' ? t('invoices.filterUnpaid') : t('invoices.filterPaid')} />
        </div>
      </div>

      {/* ── 4-KPI summary strip ── */}
      <div className="grid grid-cols-2 1080:grid-cols-4 gap-4">
        {/* Outstanding */}
        <div className="rounded-[14px] border p-[16px] shadow-[0_1px_2px_rgba(16,24,40,.04)]" style={{ background: '#FFF8F7', borderColor: '#F3C6C1' }}>
          <span className="text-[12px] font-semibold text-[#6B7280]">{t('invoices.outstanding')}{combined && <span className="text-[#9CA3AF] font-normal"> · £ combined</span>}</span>
          <div className="mt-[8px]">
            {combined ? (
              <div className="text-[22px] font-extrabold text-dr tabular-nums leading-tight truncate">
                £{outstandingGBP.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            ) : Object.keys(outstandingByCurrency).length === 0 ? (
              <span className="text-[22px] font-extrabold text-dr tabular-nums">—</span>
            ) : (
              Object.entries(outstandingByCurrency).map(([cur, val]) => (
                <div key={cur} className="text-[22px] font-extrabold text-dr tabular-nums leading-tight truncate">
                  {CURRENCY_SYMBOLS[cur] || '£'}{val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              ))
            )}
          </div>
        </div>
        {/* Received */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-[16px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
          <span className="text-[12px] font-semibold text-[#6B7280]">{t('invoices.received')}{combined && <span className="text-[#9CA3AF] font-normal"> · £ combined</span>}</span>
          <div className="mt-[8px]">
            {combined ? (
              <div className="text-[22px] font-extrabold text-sg tabular-nums leading-tight truncate">
                £{paidGBP.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            ) : Object.keys(paidByCurrency).length === 0 ? (
              <span className="text-[22px] font-extrabold text-sg tabular-nums">—</span>
            ) : (
              Object.entries(paidByCurrency).map(([cur, val]) => (
                <div key={cur} className="text-[22px] font-extrabold text-sg tabular-nums leading-tight truncate">
                  {CURRENCY_SYMBOLS[cur] || '£'}{val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              ))
            )}
          </div>
        </div>
        {/* Total Invoices */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-[16px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
          <span className="text-[12px] font-semibold text-[#6B7280]">{t('invoices.totalInvoices')}</span>
          <div className="text-[30px] font-extrabold text-navy tabular-nums mt-[8px] leading-none">{scopedInvoices.length}</div>
          <div className="text-[11.5px] text-[#9CA3AF] mt-1">{scopedInvoices.filter(i => i.status === 'paid').length} {t('invoices.paidLower')} · {scopedInvoices.filter(i => i.status === 'unpaid').length} {t('invoices.outstandingLower')}</div>
        </div>
        {/* Collection Rate */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-[16px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold text-[#6B7280]">{t('invoices.collectionRate')}</span>
            <span className="text-[13px] font-extrabold text-navy tabular-nums">{collectionRate}%</span>
          </div>
          <div className="h-[9px] bg-surface border border-[#E5E7EB] rounded-full overflow-hidden mt-[14px]">
            <div className="h-full rounded-full transition-all" style={{ width: `${collectionRate}%`, background: '#1A7A4A' }} />
          </div>
          <div className="text-[11px] text-[#9CA3AF] mt-[8px]">{t('invoices.ofTotalCollected')}</div>
        </div>
      </div>

      {/* ── Invoice list ── */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center text-[#9CA3AF] text-[13px]">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center text-[#9CA3AF] text-[13px]">{t('invoices.noneFound')}</div>
      ) : view === 'grouped' ? (
        /* ─── Grouped by request ─── */
        <div className="flex flex-col gap-[14px]">
          {groupedList.map((group) => {
            const isExpanded = expandedRequests.has(group.requestId);
            const groupPaid = group.invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (Number(i.amount) || 0), 0);
            const groupTotal = group.invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);
            const groupSym = CURRENCY_SYMBOLS[group.invoices[0]?.currency] || '£';
            const unpaidN = group.invoices.filter((i) => i.status === 'unpaid').length;
            const allPaid = unpaidN === 0;
            const pct = groupTotal > 0 ? Math.round((groupPaid / groupTotal) * 100) : 0;
            return (
              <div key={group.requestId} className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
                {/* Group header — clickable */}
                <div className="flex items-center gap-3 px-[18px] py-[16px] cursor-pointer hover:bg-surface transition-colors"
                  onClick={() => toggleRequest(group.requestId)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-[9px]">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`${base}/requests/${group.requestId}`); }}
                        className="text-[14.5px] font-extrabold text-[#111827] hover:text-pb transition text-left">
                        {tourLabel(requestsById[group.requestId], group.summary)}
                      </button>
                      {allPaid
                        ? <span className="inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-green-light text-sg"><span className="w-[5px] h-[5px] rounded-full bg-sg" />{t('invoices.allPaid')}</span>
                        : <span className="inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-amber-light text-[#92400E]"><span className="w-[5px] h-[5px] rounded-full bg-ba" />{t('invoices.nUnpaid', { n: unpaidN })}</span>
                      }
                    </div>
                    <p className="text-[12px] text-[#9CA3AF] mt-[3px]">{group.customerEmail}</p>
                  </div>
                  <div className="flex items-center gap-[18px] flex-shrink-0">
                    <div className="text-right hidden sm:block min-w-[130px]">
                      <p className="text-[11px] text-[#9CA3AF]">{t('invoices.receivedTotal')}</p>
                      <p className="text-[13.5px] font-bold tabular-nums">
                        <span className="text-sg">{fmt(groupPaid, groupSym)}</span>
                        <span className="text-[#9CA3AF]"> / {fmt(groupTotal, groupSym)}</span>
                      </p>
                      <div className="h-[5px] bg-surface rounded-full overflow-hidden mt-[5px]">
                        <div className="h-full bg-sg rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <svg className={`w-[13px] h-[13px] text-[#C7CDD6] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                    </svg>
                  </div>
                </div>

                {/* Expanded invoice table */}
                {isExpanded && (
                  <div className="border-t border-[#F1F3F6] overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-surface border-b border-[#E5E7EB]">
                        <tr>
                          {[t('invoices.colInvoice'),t('invoices.colInstalment'),t('invoices.colAmount'),t('invoices.colDue'),t('invoices.colStatus'),''].map((h, i) => (
                            <th key={i} className={`px-[18px] py-[9px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] ${i === 2 ? 'text-right' : 'text-left'}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.invoices
                          .sort((a, b) => (a.instalmentNumber ?? 0) - (b.instalmentNumber ?? 0))
                          .map((inv) => {
                            const invSym = CURRENCY_SYMBOLS[inv.currency] || '£';
                            return (
                              <tr key={inv.id} className="border-b border-[#F1F3F6] hover:bg-surface transition-colors">
                                <td className="px-[18px] py-[11px] text-[12.5px] font-bold text-[#111827] whitespace-nowrap">{inv.invoiceNumber}</td>
                                <td className="px-[18px] py-[11px] text-[12.5px] text-[#6B7280]">
                                  {inv.instalmentLabel || '—'}
                                  {inv.totalInstalments > 1 && <span className="text-[#C7CDD6] ml-1">({inv.instalmentNumber}/{inv.totalInstalments})</span>}
                                </td>
                                <td className="px-[18px] py-[11px] text-[13px] font-bold text-[#111827] tabular-nums text-right whitespace-nowrap">{fmt(inv.amount, invSym)}</td>
                                <td className="px-[18px] py-[11px] text-[12.5px] text-[#6B7280] whitespace-nowrap">
                                  {formatDate(inv.dueDate)}
                                  <AgingPill dueDate={inv.dueDate} status={inv.status} />
                                </td>
                                <td className="px-[18px] py-[11px]"><InvChip status={inv.status} /></td>
                                <td className="px-[18px] py-[11px] text-right whitespace-nowrap">
                                  <div className="inline-flex items-center gap-[14px]">
                                    <button
                                      onClick={async () => { setDownloadingId(inv.id); try { await downloadInvoicePdf(inv.id, inv.invoiceNumber); } catch { alert('PDF generation failed.'); } finally { setDownloadingId(null); } }}
                                      disabled={downloadingId === inv.id}
                                      className="text-[12.5px] font-bold text-pb hover:text-navy disabled:opacity-50 transition"
                                    >{downloadingId === inv.id ? t('common.generating') : 'PDF'}</button>
                                    {inv.status === 'unpaid'
                                      ? <button onClick={() => markPaid(inv.id)} className="text-[12.5px] font-bold text-sg hover:opacity-80 transition">{t('invoices.markPaid')}</button>
                                      : <button onClick={() => markUnpaid(inv.id)} className="text-[12.5px] font-bold text-[#9CA3AF] hover:text-[#6B7280] transition">{t('invoices.markUnpaid')}</button>
                                    }
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── Flat list ─── */
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-surface border-b border-[#E5E7EB]">
              <tr>
                {[t('invoices.colInvoice'),t('invoices.colRequest'),t('invoices.colInstalment'),t('invoices.colAmount'),t('invoices.colDue'),t('invoices.colStatus'),''].map((h, i) => (
                  <th key={i} className={`px-[18px] py-[10px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] ${i === 3 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const invSym = CURRENCY_SYMBOLS[inv.currency] || '£';
                return (
                  <tr key={inv.id} className="border-b border-[#F1F3F6] hover:bg-surface transition-colors">
                    <td className="px-[18px] py-[11px] text-[12.5px] font-bold text-[#111827] whitespace-nowrap">{inv.invoiceNumber}</td>
                    <td className="px-[18px] py-[11px] text-[13px] text-[#374151]">
                      <button onClick={() => navigate(`${base}/requests/${inv.requestId}`)} className="hover:text-pb transition text-left">{tourLabel(requestsById[inv.requestId], inv.requestSummary)}</button>
                    </td>
                    <td className="px-[18px] py-[11px] text-[12.5px] text-[#6B7280]">
                      {inv.instalmentLabel || '—'}
                      {inv.totalInstalments > 1 && <span className="text-[#C7CDD6] ml-1">({inv.instalmentNumber}/{inv.totalInstalments})</span>}
                    </td>
                    <td className="px-[18px] py-[11px] text-[13px] font-bold text-[#111827] tabular-nums text-right whitespace-nowrap">{fmt(inv.amount, invSym)}</td>
                    <td className="px-[18px] py-[11px] text-[12.5px] text-[#6B7280] whitespace-nowrap">
                      {formatDate(inv.dueDate)}
                      <AgingPill dueDate={inv.dueDate} status={inv.status} />
                    </td>
                    <td className="px-[18px] py-[11px]"><InvChip status={inv.status} /></td>
                    <td className="px-[18px] py-[11px] text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-[14px]">
                        <button
                          onClick={async () => { setDownloadingId(inv.id); try { await downloadInvoicePdf(inv.id, inv.invoiceNumber); } catch { alert('PDF generation failed.'); } finally { setDownloadingId(null); } }}
                          disabled={downloadingId === inv.id}
                          className="text-[12.5px] font-bold text-pb hover:text-navy disabled:opacity-50 transition"
                        >{downloadingId === inv.id ? t('common.generating') : 'PDF'}</button>
                        {inv.status === 'unpaid'
                          ? <button onClick={() => markPaid(inv.id)} className="text-[12.5px] font-bold text-sg hover:opacity-80 transition">{t('invoices.markPaid')}</button>
                          : <button onClick={() => markUnpaid(inv.id)} className="text-[12.5px] font-bold text-[#9CA3AF] hover:text-[#6B7280] transition">{t('invoices.markUnpaid')}</button>
                        }
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
